import React, { useEffect, useState, useContext } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Tooltip, useMap } from 'react-leaflet';
import { createPortal } from 'react-dom';
import {Typography, Button, IconButton, Menu, MenuItem, Tooltip as MuiTooltip } from "@mui/material"
import LayersIcon from '@mui/icons-material/Layers';
import { useTranslation } from 'react-i18next';
import {  IconDisplay } from '../../Utils/IconDisplay.js'; 
import L, { CRS } from 'leaflet';
import  {convertMultiPolygon, calculateCentroid} from '../../Utils/ConvertGeom.js';
import config, { environment } from '../../config';
import StateContext from '../../Contexts/StateContext';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',  
    iconSize: [25, 41],  
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
  });

  function MapComponentFunc({ dispatch, mapType, setMapType }) {
    const map = useMap();      
    const { t } = useTranslation();
    const [layersContainer, setLayersContainer] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleLayersClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMapTypeChange = (type) => {
        setMapType(type);
        handleClose();
    };

    useEffect(() => {
        const LayersControl = L.Control.extend({
            onAdd: function() {
                const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');
                const button = L.DomUtil.create('a', 'layers-button', container);
                button.href = '#';
                button.title = 'Change Map Layer';
                button.style.backgroundColor = 'white';
                button.style.width = '34px';
                button.style.height = '34px';
                button.style.display = 'flex';
                button.style.alignItems = 'center';
                button.style.justifyContent = 'center';
                button.style.cursor = 'pointer';

                L.DomEvent.disableClickPropagation(button);
                L.DomEvent.on(button, 'click', function(e) {
                    L.DomEvent.stop(e);
                    handleLayersClick(e);
                });

                setLayersContainer(button);
                return container;
            },
            onRemove: function() {
                setLayersContainer(null);
            }
        });

        const layersControl = new LayersControl({ position: 'topright' });
        map.addControl(layersControl);

        dispatch({ type: 'getMap', mapData: map });      
        return () => {
            map.removeControl(layersControl);
        };
    }, [dispatch, map]);

    return (
        <>
            {layersContainer && createPortal(
                <LayersIcon sx={{ fontSize: '24px' }} color="primary" />,
                layersContainer
            )}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <MenuItem onClick={() => handleMapTypeChange('osm')} selected={mapType === 'osm'}>
                    OpenStreetMap
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('cyclosm')} selected={mapType === 'cyclosm'}>
                    CyclOSM
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('nszn')} selected={mapType === 'nszn'}>
                    {t('Common.NsznHonismereti', 'NSZN Honismereti')}
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('herbich')} selected={mapType === 'herbich'}>
                    Herbich Geo
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('geo200000')} selected={mapType === 'geo200000'}>
                    Geo 200000
                </MenuItem>
                <MenuItem onClick={() => handleMapTypeChange('gsstdomborzat')} selected={mapType === 'gsstdomborzat'}>
                    {t('Common.GsstDomborzat', 'GSST Domborzat')}
                </MenuItem>
            </Menu>
        </>
    );
}

const KozsegVarosComponent = ({parish, index}) => {
  const map = useMap();
  let positions;
  let centroid;
  if (parish.geom.type === 'Polygon') {
      positions = parish.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (parish.geom.type === 'MultiPolygon') {
      // For MultiPolygon geometry, use the convertMultiPolygon function
      positions = convertMultiPolygon(parish.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${parish.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 11;
  return (
      <Polygon
          key={index}
          positions={positions}
          color="red"
          fillColor="#f03"
          fillOpacity={0}
          weight={1}
      >
          {map.getZoom() > zoomLevelToShowTooltip && (
                          <Tooltip permanent direction="center" opacity={1} className="polygon-label" offset={[0, 0]}>
                              <span className="polygon-sic-label">{parish.nevsic}</span><br />
                              <span className="polygon-hu-label">{parish.nev}</span><br />
                              <span className="polygon-ro-label">{parish.name}</span>
                          </Tooltip>
                      )}
      </Polygon>
  );
}

const TelepulesComponent = ({telepules, index}) => {
  const map = useMap();
  let positions;
  let centroid;
  if (telepules.geom.type === 'Polygon') {
      positions = telepules.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (telepules.geom.type === 'MultiPolygon') {
      positions = convertMultiPolygon(telepules.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${telepules.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 11;
  return (
      <Polygon
          key={index}
          positions={positions}
          color="grey"          
          fillOpacity={0.2}
      >
          {map.getZoom() > zoomLevelToShowTooltip && (
              <Tooltip permanent direction="right" opacity={0.7} className="telepules-label" offset={[0, 0]}>                  
                  <span className="telepules-hu-label">{telepules.nev}</span><br />
                  <span className="alapitva-label">{telepules.alapitva}</span>
              </Tooltip>
          )}
      </Polygon>
  );
}

const SzekComponent = ({szek, index}) => {
  const map = useMap();
  let positions;
  let centroid;
  if (szek.geom.type === 'Polygon') {
      positions = szek.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (szek.geom.type === 'MultiPolygon') {
      positions = convertMultiPolygon(szek.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${szek.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 8;
  return (
      <Polygon
          key={index}
          positions={positions}
          color="#FFD700"
          fillColor="#2492be"
          fillOpacity={0}
          weight={3}
      > 
          {map.getZoom() > zoomLevelToShowTooltip && (
              <Tooltip permanent direction="center" opacity={0.8} className="szek-label" offset={[0, 0]}>
                  <span className="szek-hu-label">{szek.nev}</span><br />
                  <span className="szek-lat-label">{szek.name}</span>
              </Tooltip>
          )}
      </Polygon>
  );
}

const MegyeComponent = ({megye, index}) => {
  const map = useMap();
  let positions;
  let centroid;
  if (megye.geom.type === 'Polygon') {
      positions = megye.geom.coordinates[0].map(coord => [coord[1], coord[0]]); // swap lat and lon
      centroid = calculateCentroid(positions); // calculate centroid
  } else if (megye.geom.type === 'MultiPolygon') {
      positions = convertMultiPolygon(megye.geom);
      centroid = calculateCentroid(positions); // calculate centroid
  } else {
      console.error(`Unsupported geometry type: ${megye.geom.type}`);
      return null;
  }
  const zoomLevelToShowTooltip = 8;
  return (
      <Polygon
          key={index}
          positions={positions}
          color="#012837" 
          fillOpacity={0}
          weight={3} 
          pathOptions={{dashArray: '5, 10'}}        
      >
          {map.getZoom() > zoomLevelToShowTooltip && (
              <Tooltip permanent direction="center" opacity={0.8} className="megye-label" offset={[0, 0]}>
                  
                  <span className="megye-ro-label">{megye.name}</span>
              </Tooltip>
          )}
      </Polygon>
  );
}

const HelynevComponent = ({helynev}) => {
  const map = useMap();
  let position;
  if (helynev.geom.type === 'Point') {
      position = [helynev.geom.coordinates[1], helynev.geom.coordinates[0]]; // swap lat and lon
  } else {
      console.error(`Unsupported geometry type: ${helynev.geom.type}`);
      return null;
  }
  const icon = IconDisplay('csucs');
  return (
      <Marker
          key={helynev.id}
          position={position} 
          icon={icon ? icon : defaultIcon }
          opacity={1}       
      >
          <Tooltip  direction="top" opacity={0.7} className="helynev-label" offset={[0, 0]}>
              <span className="helynev-hu-label">{helynev.nev}</span>
          </Tooltip>
      </Marker>
  );
}

const MapComponent = React.memo(({ allGeologicalObjects = {}, allMineralWaters = {}, allParishes = [], allTelepulesek = [],
  allSzekek = [], allMegyek = [], allHelynevek = [],  navigate, dispatch, defaultMapType, forcedMaxZoom }) => {
    const globalState = useContext(StateContext);
    const [mapType, setMapType] = useState(defaultMapType || (globalState.isSzftMember ? 'nszn' : 'osm'));

    const getTileLayer = () => {
        switch (mapType) {
            case 'herbich':
                return {
                    url: config[environment].herbichGeoTiles,
                    attribution: '&copy; Herbich Geo',
                };
            case 'nszn':
                return {
                    url: config[environment].nsznHonismeretiTiles,
                    attribution: '&copy; NSZN Honismereti',
                };
            case 'geo200000':
                return {
                    url: config[environment].geo200000Tiles,
                    attribution: '&copy; Geo 200000',
                };
            case 'gsstdomborzat':
                return {
                    url: config[environment].gsstDomborzatTiles,
                    attribution: '&copy; GSST Domborzat',
                };
            case 'cyclosm':
                return {
                    url: config[environment].cyclOSMTiles,
                    attribution: 'Map style: &copy; <a href="https://www.cyclosm.org">CyclOSM</a>',
                };
            default:
                return {
                    url: config[environment].osmTiles || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                };
        }
    };

    const tileLayerProps = getTileLayer();
    const maxZoom = forcedMaxZoom !== undefined ? forcedMaxZoom : (mapType === 'gsstdomborzat' ? 12 : 18);
    const minZoom = mapType === 'gsstdomborzat' ? 0 : 0;
    const currentZoom = 10; // default zoom from MapContainer

    return (
    <MapContainer center={[45.8643, 25.7848]} zoom={Math.min(currentZoom, maxZoom)} scrollWheelZoom={true} className='leaflet-container'
                  maxZoom={maxZoom} minZoom={minZoom}>
    <TileLayer                    
                    key={mapType}
                    attribution={tileLayerProps.attribution}
                    url={tileLayerProps.url}
                    maxZoom={maxZoom}
                    minZoom={minZoom}
                    noWrap={true}
                />
    <MapComponentFunc dispatch={dispatch} mapType={mapType} setMapType={setMapType}/>
    
    {Object.entries(allGeologicalObjects).map(([category, items]) => (
      items.map((go) => {
        const icon = IconDisplay(category);
        let position;
        
        if (go.geom.type === 'Point') {                    
          position = [go.geom.coordinates[1], go.geom.coordinates[0]];
        } else if (go.geom.type === 'MultiPolygon') {
          const coordinates = go.geom.coordinates[0][0]; 
          const sum = coordinates.reduce((acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]], [0, 0]);
          const centroid = [sum[1] / coordinates.length, sum[0] / coordinates.length]; // Corrected swap
          position = centroid;          
        } else {
          console.error(`Unsupported geometry type: ${go.geom.type}`);
          return null;
        }
        return (
          <Marker
            key={go.id}
            icon={icon ? icon : defaultIcon}
            position={position}
          >
            <Popup>
              <Typography variant="h6">{go.nev || go.name}</Typography>
              {go.name && go.nev && go.name !== go.nev && (
                <Typography variant="subtitle2" color="textSecondary">{go.name}</Typography>
              )}
              <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                {go.picture1 && (
                  <img
                    src={go.picture1}
                    alt={go.nev}
                    style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                    onClick={() => navigate(`/geological_site/${go.id}`)}
                  />
                )}
              </div>
              <Button variant="contained" fullWidth onClick={() => navigate(`/geological_site/${go.id}`)}>Részletek</Button>
            </Popup>
          </Marker>
        );
      })
    ))}  

    {Object.entries(allMineralWaters).map(([category, items]) => (
      items.map((go) => {
        const icon = IconDisplay(category);
        let position;
        
        if (go.geom.type === 'Point') {                    
          position = [go.geom.coordinates[1], go.geom.coordinates[0]];
        } else {
          console.error(`Unsupported geometry type: ${go.geom.type}`);
          return null;
        }
        return (
          <Marker
            key={`mineral-${go.id}`}
            icon={icon ? icon : defaultIcon}
            position={position}
          >
            <Popup>
              <Typography variant="h6">{go.nev || go.name || go.name_hu}</Typography>
              {(go.name || go.name_ro) && (go.nev || go.name_hu) && (go.name || go.name_ro) !== (go.nev || go.name_hu) && (
                <Typography variant="subtitle2" color="textSecondary">{go.name || go.name_ro}</Typography>
              )}
              <div style={{ maxHeight: "14rem", maxWidth: "20rem", overflow: "hidden" }}>
                {(go.picture1 || go.image) && (
                  <img
                    src={go.picture1 || go.image}
                    alt={go.nev || go.name}
                    style={{ width: "100%", height: "auto", cursor: 'pointer' }}
                    onClick={() => navigate(`/mineral_water/${category}/${go.id}`)}
                  />
                )}
              </div>
              <Button variant="contained" fullWidth onClick={() => navigate(`/mineral_water/${category}/${go.id}`)}>Részletek</Button>
            </Popup>
          </Marker>
        );
      })
    ))}  

    {allParishes.map((parish, index) => (
    <KozsegVarosComponent key={`parish-${index}`} parish={parish} index={index} />
))}
{allTelepulesek.map((telepules, index) => (
    <TelepulesComponent key={`telepules-${index}`} telepules={telepules} index={index} />
))}
{allSzekek.map((szek, index) => (
    <SzekComponent key={`szek-${index}`} szek={szek} index={index} />
))}
{allMegyek.map((megye, index) => (
    <MegyeComponent key={`megye-${index}`} megye={megye} index={index} />
))}
{allHelynevek.map((helynev, index) => (
    <HelynevComponent key={`helynev-${index}`} helynev={helynev} index={index} />
))}
   
                  
  </MapContainer>
    );
});


export default MapComponent;